import { createIsomorphicFn } from '@tanstack/react-start';
import { useMemo, useRef } from 'react';
import {
  loadQuery,
  type Environment,
  type PreloadedQuery,
  useRelayEnvironment,
} from 'react-relay';
import {
  fetchQuery,
  type ConcreteRequest,
  type FetchPolicy,
  type OperationType,
  RecordSource,
  type VariablesOf,
} from 'relay-runtime';

import { getRelayEnvironment } from '@/relay/environment';

type LoadedData<TQuery extends OperationType> =
  | {
      kind: 'server';
      variables: VariablesOf<TQuery>;
      query: ConcreteRequest;
      snapshot: ReturnType<RecordSource['toJSON']>;
      response: TQuery['response'];
    }
  | {
      kind: 'client';
      variables: VariablesOf<TQuery>;
      preloadedQuery: PreloadedQuery<TQuery>;
    };

type QueryLoaderOptions<TQuery extends OperationType> = {
  environment: Environment;
  variables: VariablesOf<TQuery>;
};

type QueryLoader<TQuery extends OperationType> = (
  options: QueryLoaderOptions<TQuery>,
) => Promise<LoadedData<TQuery>>;

export const getServerQueryResponse = <TQuery extends OperationType>(
  data: LoadedData<TQuery>,
): TQuery['response'] | null => (data.kind === 'server' ? data.response : null);

export const createQueryLoader = <TQuery extends OperationType>(
  query: ConcreteRequest,
  { fetchPolicy = 'store-and-network' }: { fetchPolicy?: FetchPolicy } = {},
): QueryLoader<TQuery> => {
  const load = createIsomorphicFn()
    .client(
      async (
        options: QueryLoaderOptions<TQuery>,
      ): Promise<LoadedData<TQuery>> => ({
        kind: 'client',
        variables: options.variables,
        preloadedQuery: loadQuery<TQuery>(
          options.environment,
          query,
          options.variables,
          { fetchPolicy },
        ),
      }),
    )
    .server(
      async (
        options: QueryLoaderOptions<TQuery>,
      ): Promise<LoadedData<TQuery>> => {
        const { environment } = getRelayEnvironment();
        const response = await fetchQuery<TQuery>(
          environment,
          query,
          options.variables,
        ).toPromise();

        return {
          kind: 'server',
          query,
          response,
          variables: options.variables,
          snapshot: environment.getStore().getSource().toJSON(),
        };
      },
    );

  return load satisfies QueryLoader<TQuery>;
};

function isLoadedData<TQuery extends OperationType>(
  data: unknown,
): data is LoadedData<TQuery> {
  if (typeof data !== 'object' || data === null || !('kind' in data)) {
    return false;
  }

  if (data.kind === 'client') {
    return 'preloadedQuery' in data && 'variables' in data;
  }

  if (data.kind === 'server') {
    return (
      'query' in data &&
      'snapshot' in data &&
      'variables' in data &&
      'response' in data
    );
  }

  return false;
}

export const useLoadedData = <TQuery extends OperationType>(
  data: unknown,
): PreloadedQuery<TQuery> => {
  if (!isLoadedData<TQuery>(data)) {
    throw new Error('Invalid data provided to useLoadedData');
  }

  const environment = useRelayEnvironment();
  const hasBeenPopulatedRef = useRef(false);

  return useMemo(() => {
    if (data.kind === 'client') {
      return data.preloadedQuery;
    }

    if (!hasBeenPopulatedRef.current) {
      hasBeenPopulatedRef.current = true;
      environment.getStore().publish(new RecordSource(data.snapshot));
    }

    return loadQuery<TQuery>(environment, data.query, data.variables, {
      fetchPolicy: 'store-only',
    });
  }, [data, environment]);
};
